/**
 * Repository Assessments Widget
 *
 * Displays recent repository assessments with health scores and action buttons.
 * Used in dashboards to show scan results and enable workflow triggers.
 *
 * Features:
 * - Health score visualization
 * - Infrastructure status badges (tests, CI, Docker)
 * - Top concerns summary
 * - Action buttons to trigger workflows
 * - Link to full assessment details
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  Badge,
  Spinner,
  Progress,
  Button,
  Tooltip,
} from 'flowbite-react';
import {
  HiCheckCircle,
  HiXCircle,
  HiExclamation,
  HiExternalLink,
  HiRefresh,
  HiPlay,
  HiShieldCheck,
  HiDocumentSearch,
  HiLightningBolt,
} from 'react-icons/hi';
import { FaGithub } from 'react-icons/fa';
import { API_ENDPOINTS } from '../services/api/endpoints';
import { Make_Authorized_API_Call, isSuccessResponse } from '../api/apiClient';

// =============================================================================
// TYPES
// =============================================================================

interface RecommendedAction {
  type: string;
  label: string;
  priority: 'high' | 'medium' | 'low';
  workflow: string;
  description: string;
}

interface Assessment {
  Assessment_ID: string;
  Repository: string;
  Status: string;
  Created_At: string;
  Completed_At: string;
  Health_Score: number;
  File_Count: number;
  Primary_Language: string;
  Frameworks: string[];
  Has_Tests: boolean;
  Has_CI: boolean;
  Has_Docker: boolean;
  Has_Infrastructure: boolean;
  Insights_Count: number;
  Concerns_Count: number;
  Top_Concerns: string[];
  GitHub_Issue: {
    Number: number;
    URL: string;
  } | null;
  Project: {
    ID: string;
    Name: string;
  } | null;
  Company: {
    ID: string;
    Name: string;
  } | null;
  Recommended_Actions: RecommendedAction[];
}

interface RepositoryAssessmentsWidgetProps {
  clientId: string;
  maxItems?: number;
  onTriggerWorkflow?: (workflow: string, repository: string) => void;
  onViewAll?: () => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

const RepositoryAssessmentsWidget: React.FC<RepositoryAssessmentsWidgetProps> = ({
  clientId,
  maxItems = 5,
  onTriggerWorkflow,
  onViewAll,
}) => {
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAssessments = useCallback(async () => {
    if (!clientId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await Make_Authorized_API_Call<Assessment>(
        API_ENDPOINTS.GITHUB_APP.ASSESSMENTS_LIST,
        'GET',
        undefined,
        { params: { Client_ID: clientId, limit: String(maxItems) } }
      );

      if (isSuccessResponse(response)) {
        const fetchedAssessments = response.data.Records || [];
        setAssessments(fetchedAssessments);
      } else {
        setError(response.message || 'Failed to load assessments');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load assessments';
      setError(errorMessage);
      console.error('[RepositoryAssessmentsWidget] Error:', err);
    } finally {
      setLoading(false);
    }
  }, [clientId, maxItems]);

  useEffect(() => {
    fetchAssessments();
  }, [fetchAssessments]);

  // =============================================================================
  // RENDER HELPERS
  // =============================================================================

  const getHealthColor = (score: number): string => {
    if (score >= 70) return 'green';
    if (score >= 40) return 'yellow';
    return 'red';
  };

  const getHealthLabel = (score: number): string => {
    if (score >= 70) return 'Healthy';
    if (score >= 40) return 'Needs Attention';
    return 'At Risk';
  };

  const getPriorityColor = (priority: string): string => {
    switch (priority) {
      case 'high': return 'failure';
      case 'medium': return 'warning';
      default: return 'gray';
    }
  };

  const getWorkflowIcon = (workflow: string) => {
    switch (workflow) {
      case 'security-review':
        return <HiShieldCheck className="w-4 h-4" />;
      case 'quality-check':
        return <HiDocumentSearch className="w-4 h-4" />;
      case 'deploy-feature':
        return <HiLightningBolt className="w-4 h-4" />;
      default:
        return <HiPlay className="w-4 h-4" />;
    }
  };

  const renderInfraStatusBadge = (hasFeature: boolean, label: string) => (
    <Tooltip content={hasFeature ? `${label} detected` : `No ${label} detected`}>
      <div className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs ${
        hasFeature ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'
      }`}>
        {hasFeature ? (
          <HiCheckCircle className="w-3 h-3" />
        ) : (
          <HiXCircle className="w-3 h-3" />
        )}
        {label}
      </div>
    </Tooltip>
  );

  // =============================================================================
  // RENDER: LOADING
  // =============================================================================

  if (loading) {
    return (
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Repository Assessments</h2>
        </div>
        <div className="flex items-center justify-center py-8">
          <Spinner size="lg" />
        </div>
      </Card>
    );
  }

  // =============================================================================
  // RENDER: ERROR
  // =============================================================================

  if (error) {
    return (
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Repository Assessments</h2>
          <Button size="xs" color="gray" onClick={fetchAssessments}>
            <HiRefresh className="w-4 h-4 mr-1" />
            Retry
          </Button>
        </div>
        <div className="text-center py-8">
          <HiExclamation className="w-12 h-12 mx-auto text-gray-400 mb-2" />
          <p className="text-gray-600">{error}</p>
        </div>
      </Card>
    );
  }

  // =============================================================================
  // RENDER: EMPTY STATE
  // =============================================================================

  if (assessments.length === 0) {
    return (
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Repository Assessments</h2>
          <Button size="xs" color="gray" onClick={fetchAssessments}>
            <HiRefresh className="w-4 h-4" />
          </Button>
        </div>
        <div className="text-center py-8">
          <FaGithub className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <p className="text-gray-600 mb-4">No repository assessments yet</p>
          <Link to="/settings/github-app">
            <Button color="blue" size="sm">
              Connect GitHub Repository
            </Button>
          </Link>
        </div>
      </Card>
    );
  }

  // =============================================================================
  // RENDER: ASSESSMENTS LIST
  // =============================================================================

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Repository Assessments</h2>
        <div className="flex gap-2">
          <Button size="xs" color="gray" onClick={fetchAssessments}>
            <HiRefresh className="w-4 h-4" />
          </Button>
          {onViewAll && (
            <Button size="xs" color="blue" onClick={onViewAll}>
              View All
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-4">
        {assessments.map((assessment) => (
          <div
            key={assessment.Assessment_ID}
            className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
          >
            {/* Header: Repository name and health score */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <FaGithub className="w-4 h-4 text-gray-600" />
                  <a
                    href={`https://github.com/${assessment.Repository}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-gray-900 hover:text-indigo-600"
                  >
                    {assessment.Repository}
                  </a>
                  <Badge color="gray" size="sm">{assessment.Primary_Language}</Badge>
                </div>
                {assessment.Project && (
                  <p className="text-xs text-gray-500">
                    Project: {assessment.Project.Name}
                    {assessment.Company && ` | ${assessment.Company.Name}`}
                  </p>
                )}
              </div>

              {/* Health Score */}
              <div className="text-center ml-4">
                <div className={`text-2xl font-bold ${
                  assessment.Health_Score >= 70 ? 'text-green-600' :
                  assessment.Health_Score >= 40 ? 'text-yellow-600' : 'text-red-600'
                }`}>
                  {assessment.Health_Score}
                </div>
                <Badge color={getHealthColor(assessment.Health_Score)} size="sm">
                  {getHealthLabel(assessment.Health_Score)}
                </Badge>
              </div>
            </div>

            {/* Health Progress Bar */}
            <div className="mb-3">
              <Progress
                progress={assessment.Health_Score}
                color={getHealthColor(assessment.Health_Score)}
                size="sm"
              />
            </div>

            {/* Infrastructure Status */}
            <div className="flex flex-wrap gap-2 mb-3">
              {renderInfraStatusBadge(assessment.Has_Tests, 'Tests')}
              {renderInfraStatusBadge(assessment.Has_CI, 'CI/CD')}
              {renderInfraStatusBadge(assessment.Has_Docker, 'Docker')}
              {renderInfraStatusBadge(assessment.Has_Infrastructure, 'IaC')}
              <div className="flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800">
                {assessment.File_Count.toLocaleString()} files
              </div>
            </div>

            {/* Top Concerns */}
            {assessment.Top_Concerns.length > 0 && (
              <div className="mb-3">
                <p className="text-xs font-medium text-gray-500 mb-1">Key Concerns:</p>
                <ul className="text-xs text-gray-700 space-y-0.5">
                  {assessment.Top_Concerns.map((concern, i) => (
                    <li key={i} className="flex items-start gap-1">
                      <span className="text-amber-500">-</span>
                      {concern}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2 pt-2 border-t">
              {/* Recommended Actions */}
              {assessment.Recommended_Actions.slice(0, 3).map((action) => (
                <Tooltip key={action.type} content={action.description}>
                  <Button
                    size="xs"
                    color={getPriorityColor(action.priority)}
                    onClick={() => onTriggerWorkflow?.(action.workflow, assessment.Repository)}
                    outline={action.priority !== 'high'}
                  >
                    {getWorkflowIcon(action.workflow)}
                    <span className="ml-1">{action.label}</span>
                  </Button>
                </Tooltip>
              ))}

              {/* View Details Link */}
              <Link
                to={`/assessment/${encodeURIComponent(assessment.Repository)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button size="xs" color="gray">
                  <HiExternalLink className="w-4 h-4 mr-1" />
                  Details
                </Button>
              </Link>

              {/* GitHub Issue Link */}
              {assessment.GitHub_Issue && (
                <a
                  href={assessment.GitHub_Issue.URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button size="xs" color="gray">
                    <FaGithub className="w-4 h-4 mr-1" />
                    Issue #{assessment.GitHub_Issue.Number}
                  </Button>
                </a>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Scan Timestamp */}
      {assessments.length > 0 && (
        <p className="text-xs text-gray-500 text-right mt-4">
          Last scan: {new Date(assessments[0].Created_At).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </p>
      )}
    </Card>
  );
};

export default RepositoryAssessmentsWidget;
